import { useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ConversationTemplate } from "@rubi/api-generated/clientRs";
import { useTranslation } from "react-i18next";

import TemplateTranslations from "./TemplateTranslations";
import type { DraftState } from "./types";

interface Props {
  template: ConversationTemplate;
  enabled: boolean;
  index: number;
  isExpanded: boolean;
  draft: DraftState | undefined;
  isSaving: boolean;
  translationLanguages: string[];
  isDraggable?: boolean;
  description?: string;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onToggleEnabled: () => void;
  onToggleEdit: () => void;
  onUpdateDraft: (updates: Partial<DraftState>) => void;
  onUpdateLangContent: (lang: string, value: string) => void;
  onUpdateTranslation: (lang: string) => void;
  onAddLang: () => void;
  onAutoTranslate: (lang: string) => void;
  onDeleteTemplate?: () => void;
  onDeleteLang: (lang: string) => void;
  onSave: () => void;
}

const TemplateRow = ({
  template,
  enabled,
  isExpanded,
  draft,
  isSaving,
  translationLanguages,
  isDraggable = true,
  description,
  onDragStart,
  onDragOver,
  onDragEnd,
  onToggleEnabled,
  onToggleEdit,
  onUpdateDraft,
  onUpdateLangContent,
  onUpdateTranslation,
  onAddLang,
  onAutoTranslate,
  onDeleteTemplate,
  onDeleteLang,
  onSave,
}: Props) => {
  const { t } = useTranslation("message");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const primaryContent = (template.content ?? []).find((c) => c.lang === "en");

  const canDrag = isDraggable && !isExpanded;

  return (
    <Box
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragOver={canDrag ? onDragOver : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      sx={{
        // The row has no fill contrast of its own — background.paper equals
        // background.default in both MUI palettes — so this border is the only
        // thing that outlines it. Hardcoded dark ink made it vanish in dark mode.
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        bgcolor: enabled ? "background.paper" : "action.hover",
        overflow: "hidden",
        transition: "background-color 0.15s",
      }}
    >
      {/* Row header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1.25,
          cursor: canDrag ? "grab" : "default",
          "&:active": { cursor: canDrag ? "grabbing" : "default" },
        }}
      >
        {isDraggable ? (
          <DragIndicatorIcon
            sx={{ color: "text.disabled", fontSize: 20, flexShrink: 0 }}
          />
        ) : (
          <Box sx={{ width: 20, flexShrink: 0 }} />
        )}
        {isExpanded && draft ? (
          <TextField
            label={t("settings.form.templateName")}
            value={draft.name}
            onChange={(e) => onUpdateDraft({ name: e.target.value })}
            onBlur={onSave}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                endAdornment: isSaving ? (
                  <CircularProgress size={14} sx={{ color: "primary.main" }} />
                ) : null,
              },
            }}
          />
        ) : (
          <Typography sx={{ flex: 1, fontSize: 14 }}>
            {template.name}
          </Typography>
        )}
        {description && (
          <Tooltip title={description} placement="top" arrow>
            <InfoOutlinedIcon
              sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }}
            />
          </Tooltip>
        )}
        <IconButton size="small" onClick={onToggleEdit}>
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {onDeleteTemplate && (
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteConfirmOpen(true)}
            disabled={isSaving}
            aria-label={t("settings.row.deleteTemplate")}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        <Switch
          checked={enabled}
          onChange={onToggleEnabled}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>

      {/* Inline edit form */}
      {isExpanded && draft && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            pl: "60px",
            pr: "12px",
            pb: "12px",
          }}
        >
          {/* Primary content */}
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t("settings.form.templateContent")}
            value={
              draft.contents.find((c) => c.lang === "en")?.content ??
              primaryContent?.content ??
              ""
            }
            onChange={(e) => onUpdateLangContent("en", e.target.value)}
            onBlur={onSave}
          />

          <TemplateTranslations
            draft={draft}
            isSaving={isSaving}
            translationLanguages={translationLanguages}
            onUpdateDraft={onUpdateDraft}
            onUpdateLangContent={onUpdateLangContent}
            onUpdateTranslation={onUpdateTranslation}
            onAddLang={onAddLang}
            onAutoTranslate={onAutoTranslate}
            onDeleteLang={onDeleteLang}
          />
        </Box>
      )}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-template-dialog-title"
        aria-describedby="delete-template-dialog-description"
      >
        <DialogTitle id="delete-template-dialog-title">
          {t("settings.row.deleteTemplateTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-template-dialog-description">
            {t("settings.row.deleteTemplateDescription", {
              name: template.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t("settings.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setDeleteConfirmOpen(false);
              onDeleteTemplate?.();
            }}
          >
            {t("settings.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateRow;
